// Какой шаблон ведомости достанется объекту. Правило ОДНО на обе вкладки карточки — «Обзор» и
// «Ведомость», — и оно то же, что у сервера.
//
// Сервер собирает ведомость объекта по шаблону, найденному тремя уровнями: свой у объекта,
// шаблон типа объекта, шаблон по умолчанию (`sites_for_payroll` в
// server/app/repositories/payroll.py). В карточку объекта приезжают только первые два:
// `effective_payroll_template_id` — это «свой, иначе от типа», про шаблон по умолчанию поле не
// знает.
//
// До 23.09.2026 вкладки читали это поле как окончательный ответ, и каждая по-своему. Аудит на
// копии боевой базы глазами финансового директора: у ПетроАльянса «Обзор» писал «шаблон не
// назначен» и «ведомость по объекту не построится», а ведомости по объекту были — собранные по
// шаблону по умолчанию. Вкладка «Ведомость» в выборе писала «сейчас — шаблон по умолчанию», а
// строкой ниже не называла никакого. Карточка спорила сама с собой.
//
// Третий уровень разрешается здесь, из списка шаблонов, который обе вкладки и так грузят.
// Контракт сервера не трогаем: у шаблона в списке уже есть признаки «по умолчанию» и «действует».

import type { SiteOut } from '@/shared/api/endpoints/org';
import type { PayrollTemplateOut } from '@/shared/api/endpoints/payroll';

/** Откуда у объекта шаблон и какой он. Уровни те же, что у сервера, и в том же порядке. */
export type ШаблонОбъекта =
  /** Задан самому объекту: правка шаблона у типа его не коснётся. */
  | { откуда: 'свой'; id: string; шаблон: PayrollTemplateOut | undefined }
  /** Своего нет, взят у типа объекта: поменяют его у типа — поедет и здесь. */
  | { откуда: 'тип'; id: string; шаблон: PayrollTemplateOut | undefined }
  /** Ни своего, ни у типа: сервер соберёт ведомость по шаблону по умолчанию. */
  | { откуда: 'по умолчанию'; id: string; шаблон: PayrollTemplateOut }
  /** Ни своего, ни у типа, ни по умолчанию: ведомость по объекту собрать не из чего. */
  | { откуда: 'нет' }
  /**
   * Своего и типового нет, а список шаблонов ещё не приехал: есть ли шаблон по умолчанию, пока
   * неизвестно. Ни «по умолчанию», ни «не построится» тут говорить нельзя — это был бы готовый
   * ответ там, где ответа ещё нет.
   */
  | { откуда: 'неизвестно' };

/** Ответ, когда список шаблонов уже на руках: «неизвестно» тогда не бывает. */
export type ИзвестныйШаблон = Exclude<ШаблонОбъекта, { откуда: 'неизвестно' }>;

type УровниОбъекта = Pick<SiteOut, 'own_payroll_template_id' | 'effective_payroll_template_id'>;

export function шаблонОбъекта(
  объект: УровниОбъекта,
  шаблоны: readonly PayrollTemplateOut[],
): ИзвестныйШаблон;
export function шаблонОбъекта(
  объект: УровниОбъекта,
  шаблоны: readonly PayrollTemplateOut[] | undefined,
): ШаблонОбъекта;
export function шаблонОбъекта(
  объект: УровниОбъекта,
  шаблоны: readonly PayrollTemplateOut[] | undefined,
): ШаблонОбъекта {
  const найти = (id: string) => шаблоны?.find((строка) => строка.id === id);

  // Первые два уровня списка не требуют: сервер уже сложил их в поля объекта.
  //
  // Сравнение нестрогое намеренно: у сервера прошлой сборки поля своего шаблона нет вовсе, и
  // `undefined` при «!== null» превращался в ложное «задан отдельно» рядом с выбором
  // «наследовать» — экран противоречил сам себе (поймано снимком 26.08.2026).
  if (объект.own_payroll_template_id != null) {
    const id = объект.own_payroll_template_id;
    return { откуда: 'свой', id, шаблон: найти(id) };
  }
  if (объект.effective_payroll_template_id != null) {
    // Своего нет, а действующий есть — значит, он от типа: поле сервера это «свой, иначе от типа».
    const id = объект.effective_payroll_template_id;
    return { откуда: 'тип', id, шаблон: найти(id) };
  }

  if (шаблоны === undefined) {
    return { откуда: 'неизвестно' };
  }
  const поУмолчанию = шаблонПоУмолчанию(шаблоны);
  return поУмолчанию === undefined
    ? { откуда: 'нет' }
    : { откуда: 'по умолчанию', id: поУмолчанию.id, шаблон: поУмолчанию };
}

/**
 * Шаблон по умолчанию — тем же правилом, что у сервера (`_шаблон_по_умолчанию`): действующий
 * шаблон с признаком «по умолчанию».
 *
 * Административные сервер при этом НЕ отсекает, и мы не отсекаем: иначе там, где признак стоит
 * только у административного, экран снова назвал бы «не построится» объект, ведомость которого
 * сервер соберёт. Но из нескольких отмеченных первым берётся объектовый: сервер берёт первый
 * попавшийся, а засев базы и перенос книг заказчика отмечают ровно один шаблон, и он объектовый.
 */
export function шаблонПоУмолчанию(
  шаблоны: readonly PayrollTemplateOut[],
): PayrollTemplateOut | undefined {
  const отмеченные = шаблоны.filter((строка) => строка.is_default && строка.is_active);
  return отмеченные.find((строка) => !строка.is_administrative) ?? отмеченные[0];
}
